import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Calendar, ChevronLeft, ChevronRight, Plus, X, Clock, User, Briefcase } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';

const DAYS_SE = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
const MONTH_SE = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfWeek = (d) => {
  const x = new Date(d);
  const wd = (x.getDay() + 6) % 7; // make Monday=0
  x.setDate(x.getDate() - wd);
  x.setHours(0, 0, 0, 0);
  return x;
};

const Schedule = () => {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    user_id: '', project_id: '', date: fmtDate(new Date()),
    start_time: '08:00', end_time: '17:00', title: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const fromDate = fmtDate(weekDays[0]);
  const toDate = fmtDate(weekDays[6]);

  useEffect(() => {
    fetchEmployees();
    fetchProjects();
  }, []);

  useEffect(() => { fetchShifts(); }, [fromDate, toDate]);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const res = await api.listShifts({ from_date: fromDate, to_date: toDate });
      setShifts(res.data || []);
    } catch (err) {
      toast.error('Kunde inte hämta schema');
    } finally { setLoading(false); }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.getEmployees();
      setEmployees((res.data || []).filter(u => u.role === 'worker' || u.role === 'admin'));
    } catch { /* ignore */ }
  };

  const fetchProjects = async () => {
    try {
      const res = await api.getProjects();
      setProjects((res.data || []).filter(p => !p.archived));
    } catch { /* ignore */ }
  };

  const openCreate = (date, userId = '') => {
    setEditing(null);
    setForm({
      user_id: userId,
      project_id: '',
      date: date || fmtDate(new Date()),
      start_time: '08:00',
      end_time: '17:00',
      title: '',
      notes: '',
    });
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      user_id: s.user_id,
      project_id: s.project_id || '',
      date: s.date,
      start_time: s.start_time,
      end_time: s.end_time,
      title: s.title || '',
      notes: s.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.user_id) { toast.error('Välj anställd'); return; }
    if (form.start_time >= form.end_time) { toast.error('Sluttid måste vara efter starttid'); return; }
    setSaving(true);
    try {
      const payload = {
        user_id: form.user_id,
        project_id: form.project_id || null,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        title: form.title || null,
        notes: form.notes || null,
      };
      if (editing) {
        await api.updateShift(editing.id, payload);
        toast.success('Pass uppdaterat');
      } else {
        await api.createShift(payload);
        toast.success('Pass skapat');
      }
      setShowForm(false);
      fetchShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara pass');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm('Ta bort detta pass?')) return;
    try {
      await api.deleteShift(editing.id);
      toast.success('Pass borttaget');
      setShowForm(false);
      fetchShifts();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const shiftsByEmpAndDay = useMemo(() => {
    const map = {};
    shifts.forEach((s) => {
      const key = `${s.user_id}_${s.date}`;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [shifts]);

  const weekLabel = `${weekDays[0].getDate()} ${MONTH_SE[weekDays[0].getMonth()]} – ${weekDays[6].getDate()} ${MONTH_SE[weekDays[6].getMonth()]} ${weekDays[6].getFullYear()}`;

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar size={22} className="text-[#E8613A]" /> Schema
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Planera arbetspass för dina anställda. Workers får automatisk push-notis när nytt pass läggs upp.
          </p>
        </div>
        <Button onClick={() => openCreate(fmtDate(new Date()))}
          className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full" data-testid="schedule-new-shift">
          <Plus size={14} className="mr-1.5" /> Nytt pass
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * 86400000))}
          className="p-2 rounded-md border border-border hover:bg-muted" data-testid="schedule-prev-week">
          <ChevronLeft size={14} />
        </button>
        <button onClick={() => setWeekStart(startOfWeek(new Date()))}
          className="text-xs px-3 h-9 rounded-md border border-border hover:bg-muted font-medium" data-testid="schedule-this-week">
          Denna vecka
        </button>
        <button onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * 86400000))}
          className="p-2 rounded-md border border-border hover:bg-muted" data-testid="schedule-next-week">
          <ChevronRight size={14} />
        </button>
        <p className="text-sm font-semibold ml-2 capitalize">{weekLabel}</p>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="schedule-grid">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground sticky left-0 bg-muted/40 z-10 w-44">
                  Anställd
                </th>
                {weekDays.map((d, i) => {
                  const isToday = fmtDate(d) === fmtDate(new Date());
                  return (
                    <th key={i} className={`text-center px-2 py-3 text-xs uppercase tracking-wider w-36 ${isToday ? 'text-[#E8613A] font-bold' : 'text-muted-foreground'}`}>
                      <div>{DAYS_SE[i]}</div>
                      <div className={`text-base mt-0.5 ${isToday ? 'text-[#E8613A]' : 'text-foreground'}`}>{d.getDate()}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-muted-foreground text-sm">
                    Inga anställda att schemalägga. Lägg till workers under "Anställda" först.
                  </td>
                </tr>
              ) : (
                employees.map((emp) => (
                  <tr key={emp.id} className="border-b border-border" data-testid={`schedule-emp-row-${emp.id}`}>
                    <td className="px-4 py-3 text-sm font-medium sticky left-0 bg-card z-10">
                      <div className="font-semibold">{emp.name || emp.email}</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{emp.role}</div>
                    </td>
                    {weekDays.map((d) => {
                      const key = `${emp.id}_${fmtDate(d)}`;
                      const dayShifts = shiftsByEmpAndDay[key] || [];
                      return (
                        <td key={key} className="px-1 py-1 align-top w-36 border-l border-border/50">
                          <div className="space-y-1 min-h-[60px]">
                            {dayShifts.map((s) => (
                              <button
                                key={s.id}
                                onClick={() => openEdit(s)}
                                className="w-full text-left p-1.5 rounded bg-[#E8613A]/10 border border-[#E8613A]/30 hover:bg-[#E8613A]/20 transition-colors"
                                data-testid={`shift-${s.id}`}
                              >
                                <p className="text-[11px] font-semibold text-[#E8613A]">{s.start_time}–{s.end_time}</p>
                                {s.title && <p className="text-[10px] text-foreground truncate">{s.title}</p>}
                                {s.project_name && <p className="text-[10px] text-muted-foreground truncate">{s.project_name}</p>}
                              </button>
                            ))}
                            <button
                              onClick={() => openCreate(fmtDate(d), emp.id)}
                              className="w-full text-[10px] text-muted-foreground hover:text-[#E8613A] py-1 rounded border border-dashed border-transparent hover:border-[#E8613A]/40 transition-colors"
                              data-testid={`schedule-add-${emp.id}-${fmtDate(d)}`}
                            >
                              + Pass
                            </button>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {loading && <p className="text-xs text-muted-foreground">Laddar…</p>}

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Redigera pass' : 'Nytt pass'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Anställd *</Label>
              <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
                data-testid="shift-user-select">
                <option value="">Välj anställd…</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name || emp.email}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Datum *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="shift-date" />
              </div>
              <div>
                <Label className="text-xs">Start *</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1" data-testid="shift-start" />
              </div>
              <div>
                <Label className="text-xs">Slut *</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" data-testid="shift-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Projekt (valfritt)</Label>
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
                data-testid="shift-project-select">
                <option value="">Inget projekt</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Titel/typ av arbete (valfritt)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="t.ex. Grävning, Schaktning" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Anteckningar (valfritt)</Label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="mt-1 w-full text-sm rounded-md border border-border bg-background px-3 py-2" />
            </div>
          </div>
          <DialogFooter>
            {editing && (
              <Button variant="outline" onClick={handleDelete} className="text-red-600 mr-auto" data-testid="shift-delete-btn">
                Ta bort
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowForm(false)}>Avbryt</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="shift-save-btn">
              {saving ? 'Sparar…' : editing ? 'Spara ändringar' : 'Skapa pass'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Schedule;
